/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - useScheduleHistory Hook
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useScheduleHistory.ts
 * @description Hook para gestión del historial de envíos ejecutados - BACKEND REAL
 * @version 2.0.0
 * @date 2025-11-28
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  ScheduleHistory,
  HistoryStats,
  HistoryFilters
} from '../types/scheduler.types';
import { useUserContext } from '../layouts/UserContext';

// ═══════════════════════════════════════════════════════════════════════════
// API CONFIG
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp/scheduler';
const API_KEY = 'anubis-whatsapp-api-key';

// Configurar axios con token
const getAuthHeaders = () => {
  const token = localStorage.getItem('hatateToken'); // ⚠️ CORREGIDO: usar hatateToken
  return {
    headers: {
      'x-api-key': API_KEY,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface UseScheduleHistoryReturn {
  // Data
  history: ScheduleHistory[];
  stats: HistoryStats | null;
  
  // Pagination
  page: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  paginatedHistory: ScheduleHistory[];
  
  // State
  isLoading: boolean;
  error: string | null;
  
  // Filters
  filters: HistoryFilters;
  setFilters: (filters: HistoryFilters) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  
  // Pagination actions
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  setPageSize: (size: number) => void;
  
  // Details
  getHistoryDetail: (id: string) => Promise<ScheduleHistory | null>;
  
  // Export
  exportHistory: (format: 'csv' | 'json') => void;
  
  // Actions
  refresh: () => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useScheduleHistory = (): UseScheduleHistoryReturn => {
  // ═══════════════════════════════════════════════════════════════════════
  // CONTEXT
  // ═══════════════════════════════════════════════════════════════════════
  
  const { user } = useUserContext();
  const phoneId = user?._id || '';
  
  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [history, setHistory] = useState<ScheduleHistory[]>([]);
  const [stats, setStats] = useState<HistoryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<HistoryFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════════════
  
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.status && filters.status.length > 0) ||
      (filters.channel && filters.channel.length > 0) ||
      filters.dateFrom !== undefined ||
      filters.dateTo !== undefined ||
      filters.campaignId !== undefined ||
      filters.minSuccessRate !== undefined
    );
  }, [filters]);

  const totalRecords = history.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const paginatedHistory = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return history.slice(startIndex, endIndex);
  }, [history, page, pageSize]);

  // ═══════════════════════════════════════════════════════════════════════
  // LOAD HISTORY - BACKEND REAL
  // ═══════════════════════════════════════════════════════════════════════
  
  const loadHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Cargando history desde API...');
      console.log('  - phoneId:', phoneId);
      
      // Construir query params
      const params = new URLSearchParams();
      params.append('phoneId', phoneId); // ⚠️ AGREGAR phoneId
      
      if (filters.status && filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      
      if (filters.channel && filters.channel.length > 0) {
        params.append('channel', filters.channel.join(','));
      }
      
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      
      if (filters.campaignId) {
        params.append('campaignId', filters.campaignId);
      }
      
      if (filters.minSuccessRate !== undefined) {
        params.append('minSuccessRate', filters.minSuccessRate.toString());
      }
      
      const url = `${API_BASE_URL}/history?${params.toString()}`;
      console.log('  - URL:', url);
      
      const response = await axios.get(url, getAuthHeaders());
      
      console.log('📊 Response history:', response.data);
      
      if (response.data && response.data.success) {
        // ⚠️ CORRECCIÓN: backend retorna response.data.history, no response.data.data
        const historyData = response.data.history || [];
        console.log('✅ History recibido:', historyData.length, 'registros');
        setHistory(historyData);
      } else {
        console.log('⚠️ Backend retornó success: false');
        throw new Error(response.data.message || 'Error al cargar historial');
      }
      
      // Reset to page 1 when filters change
      setPage(1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar historial';
      setError(message);
      console.error('❌ Error loading history:', err);
      console.error('  - Full error:', err);
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // LOAD STATS - BACKEND REAL
  // ═══════════════════════════════════════════════════════════════════════
  
  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/stats`,
        getAuthHeaders()
      );
      
      if (response.data && response.data.success) {
        // Si el backend retorna stats de history específicamente
        const statsData = response.data.data.history || response.data.data;
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      // Si falla, calcular stats localmente desde history
      if (history.length > 0) {
        const successful = history.filter(h => h.status === 'success').length;
        const failed = history.filter(h => h.status === 'failed').length;
        const partial = history.filter(h => h.status === 'partial').length;
        
        const totalMessagesSent = history.reduce((sum, h) => sum + h.stats.sent, 0);
        const totalMessagesFailed = history.reduce((sum, h) => sum + h.stats.failed, 0);
        const averageDuration = history.reduce((sum, h) => sum + h.stats.duration, 0) / history.length;
        const averageSuccessRate = history.reduce((sum, h) => sum + h.stats.successRate, 0) / history.length;
        
        // Calcular byChannel
        const byChannel: Record<string, { total: number; successful: number; failed: number }> = {};
        history.forEach(h => {
          if (!byChannel[h.channel]) {
            byChannel[h.channel] = { total: 0, successful: 0, failed: 0 };
          }
          byChannel[h.channel].total++;
          if (h.status === 'success') byChannel[h.channel].successful++;
          if (h.status === 'failed') byChannel[h.channel].failed++;
        });
        
        const calculated: HistoryStats = {
          total: history.length,
          successful,
          failed,
          partial,
          averageDuration,
          averageSuccessRate,
          totalMessagesSent,
          totalMessagesFailed,
          byChannel
        };
        setStats(calculated);
      }
    }
  }, [history]);

  // ═══════════════════════════════════════════════════════════════════════
  // FILTERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // PAGINATION
  // ═══════════════════════════════════════════════════════════════════════
  
  const goToPage = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }, [totalPages]);

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  }, [page, totalPages]);

  const previousPage = useCallback(() => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  }, [page]);

  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    setPage(1); // Reset to first page
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // GET DETAIL - BACKEND REAL
  // ═══════════════════════════════════════════════════════════════════════
  
  const getHistoryDetail = useCallback(async (id: string): Promise<ScheduleHistory | null> => {
    try {
      setError(null);
      
      const response = await axios.get(
        `${API_BASE_URL}/history/${id}`,
        getAuthHeaders()
      );
      
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error al obtener detalle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al obtener detalle';
      setError(message);
      console.error('Error getting history detail:', err);
      return null;
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // EXPORT
  // ═══════════════════════════════════════════════════════════════════════
  
  const exportHistory = useCallback((format: 'csv' | 'json') => {
    try {
      let content: string;
      let filename: string;
      let mimeType: string;

      if (format === 'csv') {
        // Generate CSV
        const headers = [
          'ID',
          'Ejecutado',
          'Canal',
          'Campaña',
          'Total',
          'Enviados',
          'Fallidos',
          'Tasa Éxito',
          'Duración',
          'Estado'
        ];
        
        const rows = history.map(h => [
          h.id,
          h.executedAt,
          h.channel,
          h.campaignName || '-',
          h.stats.total,
          h.stats.sent,
          h.stats.failed,
          `${h.stats.successRate.toFixed(2)}%`,
          `${h.stats.duration}ms`,
          h.status
        ]);

        content = [
          headers.join(','),
          ...rows.map(row => row.join(','))
        ].join('\n');

        filename = `historial_envios_${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      } else {
        // Generate JSON
        content = JSON.stringify(history, null, 2);
        filename = `historial_envios_${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
      }

      // Create download link
      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting history:', err);
      setError('Error al exportar historial');
    }
  }, [history]);

  // ═══════════════════════════════════════════════════════════════════════
  // REFRESH
  // ═══════════════════════════════════════════════════════════════════════
  
  const refresh = useCallback(async () => {
    await Promise.all([loadHistory(), loadStats()]);
  }, [loadHistory, loadStats]);

  // ═══════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════
  
  // Load initial data
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  
  // Load stats when history changes
  useEffect(() => {
    if (history.length > 0) {
      loadStats();
    }
  }, [history, loadStats]);

  // ═══════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════
  
  return {
    // Data
    history,
    stats,
    
    // Pagination
    page,
    pageSize,
    totalPages,
    totalRecords,
    paginatedHistory,
    
    // State
    isLoading,
    error,
    
    // Filters
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    
    // Pagination actions
    goToPage,
    nextPage,
    previousPage,
    setPageSize: handleSetPageSize,
    
    // Details
    getHistoryDetail,
    
    // Export
    exportHistory,
    
    // Actions
    refresh
  };
};

export default useScheduleHistory;